/**
 * Shared password validation utilities
 * Ensures consistency across all password validation in the application
 */

// Cognito's documented symbol set for the RequireSymbols policy. Dash is
// accepted in practice though it isn't in the AWS-docs list — included so users
// who use a dash in their password aren't told (incorrectly) that they have no
// symbol. Must stay in sync with backend/application.yaml PasswordPolicy.
const SYMBOL_REGEX = /[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`+=-]/

/**
 * Validates password according to Cognito password policy:
 * - Minimum 8 characters
 * - At least one lowercase letter
 * - At least one uppercase letter
 * - At least one number
 * - At least one symbol
 *
 * @param {string} password - The password to validate
 * @returns {boolean} - True if password meets all requirements
 */
export function isValidPassword (password) {
  const pwd = password || ''
  const hasMinLength = pwd.length >= 8
  const hasLowercase = /[a-z]/.test(pwd)
  const hasUppercase = /[A-Z]/.test(pwd)
  const hasNumber = /[0-9]/.test(pwd)
  const hasSymbol = SYMBOL_REGEX.test(pwd)

  return hasMinLength && hasLowercase && hasUppercase && hasNumber && hasSymbol
}

/**
 * Returns a validation error message for password policy violations
 * @param {string} password - The password to validate
 * @returns {string|null} - Error message if invalid, null if valid
 */
export function getPasswordValidationError (password) {
  if (isValidPassword(password)) {
    return null
  }

  const pwd = password || ''
  const hasMinLength = pwd.length >= 8
  const hasLowercase = /[a-z]/.test(pwd)
  const hasUppercase = /[A-Z]/.test(pwd)
  const hasNumber = /[0-9]/.test(pwd)
  const hasSymbol = SYMBOL_REGEX.test(pwd)

  if (!hasMinLength) {
    return 'Password must be at least 8 characters long'
  }

  const missing = []
  if (!hasLowercase) missing.push('lowercase letter')
  if (!hasUppercase) missing.push('uppercase letter')
  if (!hasNumber) missing.push('number')
  if (!hasSymbol) missing.push('symbol')

  return `Password must include at least one ${missing.join(', ').replace(/, ([^,]+)$/, ', and $1')}`
}

/**
 * Vuetify validation rule for password policy
 * Can be used directly in :rules arrays
 * @param {string} value - The password value to validate
 * @returns {boolean|string} - True if valid, error message if invalid
 */
export function passwordValidationRule (value) {
  const error = getPasswordValidationError(value)
  return error || true
}

/**
 * Password hint text for UI components
 */
export const PASSWORD_HINT = 'At least 8 characters, including a lowercase letter, an uppercase letter, a number, and a symbol (e.g. ! @ # $ %)'
